import React from 'react';
import { AF_AREA_PATH, AF_ASSIGNED_TO, AF_CAPEX, AF_DEMO, AF_GOV_PROJECT, AF_GUESTIMATE, AF_INITIATIV, AF_PLANNING_PRIORITY, AF_PLANNING_STATUS, AF_PRIORITY_DRIVER, AF_STATE, AF_TAGS, AF_TEAMS, AF_PRODUCT, AF_INVESTMENT_CLASS, Ticket } from '../../services/Ticket';
import { formatDate } from '../../utils/formatDate';
import { ADO_PLANNING_STATUSES, TEAMS_LIST } from '../../Common/Constants';
import { ProjectWorkItemData } from '../../services/AzureDevOpsService';
import { Button, InputNumber, Select, Tooltip, Input } from 'antd';
import { EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import './ChartMenuForm.css';
import AzureDevOpsService from '../../services/AzureDevOpsService';
import InheritedFields from './InheritedFields';

interface ChartMenuFormProps {
  selectedTicket: Ticket;
  onUpdateTicket?: (ticketId: number, updatedFields: Record<string, any>) => void;
  onSetRefresh: () => void;
  auth?: any; // Replace `any` with a more specific type if available
  onRefreshChildren: () => void;
  onClose: () => void;
  showTeams : boolean;
  useAdvancedMode: boolean; // New prop for advanced mode
  projectWorkItemData: ProjectWorkItemData[];
  usersData: any[]; // Pre-fetched users data
  capexOptions: string[]; // Dynamic CapEx options
  govProjectOptions: string[]; // Dynamic Gov Project options
  priorityDriverOptions: string[]; // Dynamic Priority Driver options
  demoOptions: string[]; // Dynamic Demo options
  productOptions: string[]; // Dynamic Product options
  investmentClassOptions: string[]; // Dynamic Investment Class options
  areaPathOptions?: string[]; // Dynamic Area Path options (optional, only for RnD Planning)
  azureDevOpsService: AzureDevOpsService; // Add AzureDevOpsService instance
  showComments: boolean; // New prop for comments visibility
  iterationsData: any[]; // Add iterations data
  onFormChangedStateChange?: (isChanged: boolean) => void; // New prop to notify parent of change state
  onExecuteFormSave?: (saveFunc: () => Promise<void>) => void; // New prop to expose save function
}

const ChartMenuForm: React.FC<ChartMenuFormProps> = (props) => {
  const getPagePrefix = (): string => {
    const pathname = window.location.pathname;
    if (pathname.includes('/team-planning')) return 'TeamPlanning';
    if (pathname.includes('/rnd-planning')) return 'RnDPlanning';
    if (pathname.includes('/time-tracking')) return 'TimeTracking';
    if (pathname.includes('/triage')) return 'Triage';
    return 'Default'; // Fallback for other pages
  };

  const [showFormControls, setShowFormControls] = React.useState<boolean>(() => {
    const pagePrefix = getPagePrefix();
    const storageKey = `${pagePrefix}-showFormControls`;
    try {
      const stored = localStorage.getItem(storageKey);
      return stored !== null ? JSON.parse(stored) : true; // Default to true if not set
    } catch (error) {
      console.warn(`Failed to load ${storageKey} from localStorage:`, error);
      return true; // Default to true on error
    }
  });

  React.useEffect(() => {
    const pagePrefix = getPagePrefix();
    const storageKey = `${pagePrefix}-showFormControls`;
    try {
      localStorage.setItem(storageKey, JSON.stringify(showFormControls));
    } catch (storageError) {
      console.warn(`Failed to save ${storageKey} due to storage quota:`, storageError);
    }
  }, [showFormControls]);


  const fieldsToUpdate = () => {
    return {
      [AF_ASSIGNED_TO]: formAssignee ? { displayName: formAssignee.label, uniqueName: formAssignee.value } : "",
      [AF_CAPEX]: formCapex, // Assuming capex is a string
      [AF_GOV_PROJECT]: formGovProject, // Include government project in the update
      [AF_GUESTIMATE]: formGuestimate,
      [AF_INITIATIV]: formInitiativeName,
      [AF_PLANNING_PRIORITY]: formPlanningPriority,
      [AF_PLANNING_STATUS]: formPlanningStatus,
      [AF_PRIORITY_DRIVER]: formPriorityDriver, // Include priority driver in the update
      [AF_STATE]: formStatus,
      [AF_TEAMS]: teams.join(";"),
      [AF_DEMO]: formDemo,
      [AF_PRODUCT]: formProduct,
      [AF_INVESTMENT_CLASS]: formInvestmentClass,
      [AF_AREA_PATH]: formAreaPath, // Include area path in the update
      ["System.IterationPath"]: formIterationPath || props.selectedTicket.project || "",
      [AF_TAGS]: formTags.join(";"),
    };
  };
  const [formAssignee, setFormAssignee] = React.useState<{label: string, value: string} | null>(
    props.selectedTicket.assignee && props.selectedTicket.assignee !== "<Unassigned>" 
      ? { label: props.selectedTicket.assignee, value: props.selectedTicket.assigneeUniqueName || props.selectedTicket.assignee }
      : null
  );
  const userOptions = React.useMemo(() => {
    return props.usersData.map((user: any) => ({
      label: user.displayName || user.principalName,
      value: user.principalName || user.mailAddress
    }));
  }, [props.usersData]);
  
  const [formCapex, setFormCapex] = React.useState<string>(props.selectedTicket.capex || ""); // Assuming capex is a string
  const [formGovProject, setFormGovProject] = React.useState<string>(props.selectedTicket.govProject || ""); // New state for government project
  const [formDemo, setFormDemo] = React.useState<string>(props.selectedTicket.demo || "");
  const [formProduct, setFormProduct] = React.useState<string>(props.selectedTicket.product || "");
  const [formInvestmentClass, setFormInvestmentClass] = React.useState<string>(props.selectedTicket.investmentClass || "");
  const [formAreaPath, setFormAreaPath] = React.useState<string>(props.selectedTicket.area || "");
  const [formGuestimate, setFormGuestimate] = React.useState<number>(props.selectedTicket.guestimate || 0);
  const [formInitiativeName, setFormInitiativeName] = React.useState<string>(props.selectedTicket.initiative || "");
  const [formPlanningPriority, setFormPlanningPriority] = React.useState<number>(props.selectedTicket.planningPri || 0);
  const [formPlanningStatus, setFormPlanningStatus] = React.useState<string>(props.selectedTicket.planningStatus || "0. Not Started");
  const [formPriorityDriver, setFormPriorityDriver] = React.useState<string>(props.selectedTicket.priorityDriver || ""); // New state for priority driver
  const [teams, setTeams] = React.useState<string[]>(props.selectedTicket.teamList || []);
  const [formStatus, setFormStatus] = React.useState<string>(props.selectedTicket.state || "");
  const [formComment, setFormComment] = React.useState<string>(props.selectedTicket.description || "");
  const [formIterationPath, setFormIterationPath] = React.useState<string>(props.selectedTicket.fields?.["System.IterationPath"] || "");
  const [formTags, setFormTags] = React.useState<string[]>(
    props.selectedTicket.tags ? props.selectedTicket.tags.split(';').map(t => t.trim()).filter(t => t !== '') : []
  );

  const planningStatusOptions = ADO_PLANNING_STATUSES.map((status) => ({
    value: status.value,
    label: status.label,
  }));

  const getStatusOptions = () => {
    if (!props.projectWorkItemData || props.projectWorkItemData.length === 0) {
      return [{ value: props.selectedTicket.state, label: props.selectedTicket.state }];
    }
    
    const projectData = props.projectWorkItemData.find(p => p.project.toLowerCase() === props.selectedTicket.project.toLowerCase());
    if (projectData && projectData.workItemStates[props.selectedTicket.type]) {
      return projectData.workItemStates[props.selectedTicket.type].map(state => ({
        value: state.name,
        label: state.name
      }));
    }
    
    return [{ value: props.selectedTicket.state, label: props.selectedTicket.state }];
  };

  const statusOptions = getStatusOptions();



  const isChanged: boolean = (
    formAssignee?.value !== (props.selectedTicket.assigneeUniqueName || props.selectedTicket.assignee || "")
    || formPlanningStatus !== (props.selectedTicket.planningStatus || "0. Not Started") 
    || formInitiativeName !== (props.selectedTicket.initiative || "")
    || formPlanningPriority !== (props.selectedTicket.planningPri || 0)
    || formGuestimate !== (props.selectedTicket.guestimate || 0)
    || teams.length !== (props.selectedTicket.teamList?.length || 0)
    || !teams.every((team, idx) => team === (props.selectedTicket.teamList?.[idx] || ""))
    || formPriorityDriver !== (props.selectedTicket.priorityDriver || "")
    || formGovProject !== (props.selectedTicket.govProject || "")
    || formCapex !== (props.selectedTicket.capex || "")
    || formDemo !== (props.selectedTicket.demo || "")
    || formProduct !== (props.selectedTicket.product || "")
    || formInvestmentClass !== (props.selectedTicket.investmentClass || "")
    || formAreaPath !== (props.selectedTicket.area || "")
    || formStatus !== (props.selectedTicket.state || "")
    || (formComment.trim() && formComment.trim() !== (props.selectedTicket.description || "").trim())
    || formIterationPath !== (props.selectedTicket.fields?.["System.IterationPath"] || "")
    || formTags.join(';') !== (props.selectedTicket.tags || "")
  );

  const executeFormSave = React.useCallback(async () => {
    if (props.onUpdateTicket) {
      const updatedFields: any = fieldsToUpdate();
      await props.onUpdateTicket(props.selectedTicket.id, updatedFields);
    }
    
    const trimmedComment = formComment.trim();
    if (trimmedComment && trimmedComment !== (props.selectedTicket.description || "").trim()) {
      await props.azureDevOpsService.addComment(props.selectedTicket.id, trimmedComment);
    }
  }, [props.onUpdateTicket, props.selectedTicket.id, fieldsToUpdate, formComment, props.azureDevOpsService]);

  React.useEffect(() => {
    props.onExecuteFormSave?.(executeFormSave);
  }, [executeFormSave, props.onExecuteFormSave]);

  React.useEffect(() => {
    props.onFormChangedStateChange?.(isChanged);
  }, [isChanged, props.onFormChangedStateChange]);

  return (
    <div className="chart-menu-form">
      <div className="period-info">
        <div className="period-text">
          <strong>Period:</strong> {props.selectedTicket.startIteration ? formatDate(props.selectedTicket.startIteration) : ""} - {props.selectedTicket.endIteration ? formatDate(props.selectedTicket.endIteration) : ""}
        </div>
        
        <div className="status-section">
          <div className="status-field">
            <label className="status-label">Status:</label>
            <Select
              size="small"
              value={formStatus || undefined}
              onChange={(value) => {
                if (value) {
                  setFormStatus(value);
                }
              }}
              disabled={!props.projectWorkItemData || props.projectWorkItemData.length === 0}
              style={{ minWidth: '160px', maxWidth: '280px' }}
            >
              {statusOptions.map((option) => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
            {(!props.projectWorkItemData || props.projectWorkItemData.length === 0) && (
              <span className="status-loading-text">(Loading project data...)</span>
            )}
          </div>
          
          <div className="planning-priority-field">
            <label className="status-label">Planning Pri:</label>
            <InputNumber
              size="small"
              value={formPlanningPriority}
              onChange={(value: number | null) => setFormPlanningPriority(value || 0)}
              min={-100}
              max={100000}
            />
          </div>
          
          <div className="guestimate-field">
            <label className="status-label">Guestimate:</label>
            <InputNumber
              size="small"
              value={formGuestimate}
              onChange={(value: number | null) => setFormGuestimate(value || 0)}
              min={0}
              max={100}
            />
          </div>

          <div className="iteration-field">
            <label className="status-label" style={{ marginRight: '8px' }}>Iteration path:</label>
            <Tooltip
              title={props.iterationsData.filter(iteration => iteration.projectName === props.selectedTicket.project).length === 0 
                ? "No available iterations for this project" 
                : ""
              }
            >
              <Select
                size="small"
                value={formIterationPath || undefined}
                onChange={(value) => {
                  const iterationValue = value || props.selectedTicket.project || "";
                  setFormIterationPath(iterationValue);
                }}
                allowClear
                showSearch
                placeholder="Select iteration..."
                style={{ minWidth: '200px', maxWidth: '300px' }}
                disabled={props.iterationsData.filter(iteration => iteration.projectName === props.selectedTicket.project).length === 0}
              >
                {props.iterationsData
                  .filter(iteration => iteration.projectName === props.selectedTicket.project)
                  .map((iteration) => (
                    <Select.Option key={iteration.id} value={iteration.path || iteration.name}>
                      {iteration.name}
                    </Select.Option>
                  ))}
              </Select>
            </Tooltip>
          </div>
        </div>

        <Button
          type="text"
          size="small"
          onClick={() => setShowFormControls(!showFormControls)}
          className="toggle-controls-btn"
        >
          {showFormControls ? <EyeInvisibleOutlined /> : <EyeOutlined />}
          {showFormControls ? ' Hide' : ' Show'}
        </Button>
      </div>

      {showFormControls && (
        <div className="form-content">
        <div className="form-section">
          <div className="assignee-initiative-grid">
            <div className="form-item assignee-field">
              <label className="form-label">Assignee</label>
              <Select
                size="small"
                style={{ width: '100%' }}
                value={formAssignee?.value || undefined}
                onChange={(value) => {
                  const selectedUser = userOptions.find(user => user.value === value);
                  setFormAssignee(selectedUser || null);
                }}
                allowClear
                showSearch
                placeholder="Select user..."
                notFoundContent="No users found"
              >
                {userOptions.map((option) => (
                  <Select.Option key={option.value} value={option.value}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>
            </div>

            <div className="form-item initiative-field">
              <label className="form-label">Initiative Name</label>
              <Input
                id="initiativeNameInput"
                value={formInitiativeName}
                onChange={(e) => {
                  setFormInitiativeName(e.target.value);
                }}
                className="initiative-name-input"
                style={{ width: '100%' }}
              />
            </div>
            
            {props.areaPathOptions && props.areaPathOptions.length > 0 && (
              <div className="form-item">
                <label className="form-label">Area Path</label>
                <Select
                  size="small"
                  value={(() => {
                    // Extract first-level for display (e.g., "RnD Planning\\TeamA" -> "TeamA")
                    const parts = formAreaPath?.split('\\') || [];
                    return parts.length > 1 ? parts[1] : (parts.length === 1 && parts[0] ? "/" : formAreaPath);
                  })()}
                  showSearch
                  onChange={(value) => {
                    if (value === "/") {
                      // Set to root (just the project name)
                      const rootArea = props.selectedTicket.area?.split('\\')[0] || props.selectedTicket.project;
                      setFormAreaPath(rootArea);
                    } else if (value) {
                      // Construct full path (e.g., "TeamA" -> "RnD Planning\\TeamA")
                      const rootArea = props.selectedTicket.area?.split('\\')[0] || props.selectedTicket.project;
                      setFormAreaPath(`${rootArea}\\${value}`);
                    } else {
                      setFormAreaPath("");
                    }
                  }}
                >
                  <Select.Option key="/" value="/">
                    /
                  </Select.Option>
                  {props.areaPathOptions.map((option) => (
                    <Select.Option key={option} value={option}>
                      {option}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            )}
          </div>

          <div className="planning-fields-grid">
            <div className="form-item">
              <label className="form-label">Planning Status</label>
              <Select
                size="small"
                value={formPlanningStatus || undefined}
                onChange={(value) => {
                  if (value) {
                    setFormPlanningStatus(value);
                  }
                }}
              >
                {planningStatusOptions.map((option) => (
                  <Select.Option key={option.value} value={option.value}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>
            </div>

            <div className="form-item">
              <label className="form-label">Demo</label>
              <Select
                size="small"
                value={formDemo || undefined}
                allowClear
                showSearch
                onChange={(value) => {
                  setFormDemo(value || "");
                }}
              >
                {props.demoOptions.map((option) => (
                  <Select.Option key={option} value={option}>
                    {option}
                  </Select.Option>
                ))}
              </Select>
            </div>

            <div className="form-item project-info-field">
              <label className="form-label">Project</label>
              <div className="project-info-display">
                {props.selectedTicket.project || "<unknown>"}
              </div>
            </div>
          </div>
        </div>

        {props.useAdvancedMode && (
          <div className="form-section">
            <div className="advanced-fields-grid">
              <div className="form-item">
                <label className="form-label">
                  Priority Driver
                  <InheritedFields 
                    selectedTicket={props.selectedTicket}
                    azureDevOpsService={props.azureDevOpsService}
                    fieldType="priorityDriver"
                  />
                </label>
                <Select
                  size="small"
                  value={formPriorityDriver || undefined}
                  allowClear
                  showSearch
                  onChange={(value) => {
                    setFormPriorityDriver(value || "");
                  }}
                >
                  {props.priorityDriverOptions.map((driver) => (
                    <Select.Option key={driver} value={driver}>
                      {driver}
                    </Select.Option>
                  ))}
                </Select>
              </div>

              <div className="form-item">
                <label className="form-label">
                  Gov Project
                  <InheritedFields 
                    selectedTicket={props.selectedTicket}
                    azureDevOpsService={props.azureDevOpsService}
                    fieldType="govProject"
                  />
                </label>
                <Select
                  size="small"
                  value={formGovProject || undefined}
                  allowClear
                  showSearch
                  onChange={(value) => {
                    setFormGovProject(value || "");
                  }}
                >
                  {props.govProjectOptions.map((option) => (
                    <Select.Option key={option} value={option}>
                      {option}
                    </Select.Option>
                  ))}
                </Select>
              </div>

              <div className="form-item">
                <label className="form-label">
                  CapEx
                  <InheritedFields 
                    selectedTicket={props.selectedTicket}
                    azureDevOpsService={props.azureDevOpsService}
                    fieldType="capex"
                  />
                </label>
                <Select
                  size="small"
                  value={formCapex || undefined}
                  allowClear
                  showSearch
                  onChange={(value) => {
                    setFormCapex(value || "");
                  }}
                >
                  {props.capexOptions.map((option) => (
                    <Select.Option key={option} value={option}>
                      {option}
                    </Select.Option>
                  ))}
                </Select>
              </div>

              <div className="form-item">
                <label className="form-label">
                  Product
                  <InheritedFields 
                    selectedTicket={props.selectedTicket}
                    azureDevOpsService={props.azureDevOpsService}
                    fieldType="product"
                  />
                </label>
                <Select
                  size="small"
                  value={formProduct || undefined}
                  allowClear
                  showSearch
                  onChange={(value) => {
                    setFormProduct(value || "");
                  }}
                >
                  {props.productOptions.map((option) => (
                    <Select.Option key={option} value={option}>
                      {option}
                    </Select.Option>
                  ))}
                </Select>
              </div>

              <div className="form-item">
                <label className="form-label">
                  Investment Class
                  <InheritedFields 
                    selectedTicket={props.selectedTicket}
                    azureDevOpsService={props.azureDevOpsService}
                    fieldType="investmentClass"
                  />
                </label>
                <Select
                  size="small"
                  value={formInvestmentClass || undefined}
                  allowClear
                  showSearch
                  onChange={(value) => {
                    setFormInvestmentClass(value || "");
                  }}
                >
                  {props.investmentClassOptions.map((option) => (
                    <Select.Option key={option} value={option}>
                      {option}
                    </Select.Option>
                  ))}
                </Select>
              </div>

              

            </div>
          </div>
        )}


        {props.showTeams && (
          <div className="form-section">
            <div className="form-item">
              <label className="form-label">Teams</label>
              <Select
                {...({ mode: "multiple" } as any)}
                size="small"
                value={teams}
                onChange={(value: any) => setTeams(value || [])}
                allowClear
                showSearch
              >
                {TEAMS_LIST.map((option) => (
                  <Select.Option key={option.team} value={option.team}>
                    {option.team}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </div>
        )}

        <div className="form-section">
          <div className="form-item">
            <label className="form-label">Tags</label>
            <Select
              {...({ mode: "tags" } as any)}
              size="small"
              value={formTags}
              onChange={(value: string[]) => setFormTags(value || [])}
              placeholder="Add tags..."
              style={{ width: '100%' }}
            />
          </div>
        </div>

        {props.showComments && (
          <div className="form-section">
            <div className="form-item">
              <label className="form-label">Comments</label>
              <Input.TextArea
                value={formComment}
                onChange={(e) => setFormComment(e.target.value)}
                rows={4}
                placeholder="Add comments..."
                style={{ width: '100%' }}
              />
            </div>
          </div>
        )}

        </div>
      )}
    </div>
  );
};

export default ChartMenuForm;
